import { useJobillaOrgId } from '@/hooks'
import { DocumentArrowUpIcon } from '@heroicons/react/20/solid'
import { useOrganisationRelationShow } from '@jobilla/nexus-sdk'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { LiveField, RichLiveField, StatefulImage } from '../rapid-ui'
import { ThankYouScreen } from '../views'
import { campaignDetailsBrandingAtom } from './state'

type ThankYouPageSimulatorProps = {
	screen: ThankYouScreen | undefined
	isGenerating: boolean
	isLoading: boolean
	onTitleChange: (title: string) => void
	onDescriptionChange: (description: string) => void
}

export function ThankYouPageSimulator({
	screen,
	isGenerating,
	isLoading,
	onTitleChange,
	onDescriptionChange,
}: ThankYouPageSimulatorProps) {
	const { t } = useTranslation('editor')
	const activeBranding = useAtomValue(campaignDetailsBrandingAtom)
	const organisationId = useJobillaOrgId()
	const { data: organisation } = useOrganisationRelationShow(organisationId, { enabled: !!organisationId })
	const collectsCv = organisation?.candidateProfileCvCollectionEnabled === true

	if (!screen) {
		return <ThankYouPageEmptyState />
	}
	return (
		<div
			style={{
				width: '500px',
				fontFamily: 'Onest',
			}}
			className="ring perspective-dramatic ring-grey-200 bg-white flex flex-col rounded-sm shadow-lg"
		>
			<div className="flex flex-col gap-6 p-10">
				{activeBranding?.logo?.url && (
					<div className="flex items-center justify-center mx-auto">
						<StatefulImage
							src={activeBranding.logo.url}
							alt="Logo"
							className="h-24 w-24"
							objectFit="contain"
						/>
					</div>
				)}
				<div className="text-2xl font-semibold text-center">
					<LiveField
						isGenerating={isGenerating || isLoading}
						value={screen.title}
						onChange={onTitleChange}
					/>
				</div>
				<div className="text-grey-600">
					<RichLiveField
						isGenerating={isGenerating || isLoading}
						value={screen.description}
						onChange={onDescriptionChange}
						disableMedia
					/>
				</div>
				<div data-additional-questions-hint className="mt-4 flex flex-col gap-3 animate-pulse">
					<p className={'text-gray-700 text-sm'}>{t('simulator.thankYouPage.additionalQuestionsHint')}</p>
					<div className="h-3 bg-purple-200 rounded w-1/3"></div>
					<div className="rounded-md border border-purple-200 bg-purple-50 px-3 py-2.5">
						<div className="h-3 bg-purple-200/60 rounded w-2/3"></div>
					</div>
					<div className="h-3 bg-purple-200 rounded w-2/5 mt-1"></div>
					<div className="rounded-md border border-purple-200 bg-purple-50 px-3 py-2.5">
						<div className="h-3 bg-purple-200/60 rounded w-1/2"></div>
					</div>
				</div>
				{collectsCv
					? (
						<div className="flex items-center gap-3 rounded-md border border-purple-200 bg-purple-50 px-3.5 py-3 text-purple-900">
							<DocumentArrowUpIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
							<p className="text-sm font-semibold">{t('candidateProfile.cvUpload')}</p>
						</div>
					)
					: null}
			</div>
			{(screen.logo || screen.organisation_name) && (
				<div className="flex flex-col items-center">
					<div className="w-3/4 border-t border-grey-100" />
					<div className="px-10 py-5 flex items-center justify-center gap-2">
						{screen.logo?.url && (
							<img
								src={screen.logo.url}
								alt={screen.organisation_name ?? t('simulator.thankYouPage.organisationLogoAlt')}
								className="h-5 w-auto object-contain"
							/>
						)}
						{screen.organisation_name && (
							<span className="text-grey-400 text-sm">
								{screen.organisation_name}
							</span>
						)}
					</div>
				</div>
			)}
		</div>
	)
}

function ThankYouPageEmptyState() {
	const { t } = useTranslation('editor')
	return (
		<div
			style={{
				width: '500px',
				fontFamily: 'Onest',
			}}
			className="ring perspective-dramatic ring-grey-200 bg-white flex flex-col rounded-sm shadow-lg"
		>
			<div className="flex flex-col items-center gap-4 p-10 text-center">
				<div className="flex items-center justify-center w-16 h-16 rounded-full bg-grey-100">
					<svg
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 24 24"
						strokeWidth={1.5}
						stroke="currentColor"
						className="w-8 h-8 text-grey-400"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
						/>
					</svg>
				</div>
				<div>
					<p className="text-grey-700 font-medium">{t('simulator.thankYouPage.noScreen')}</p>
					<p className="text-grey-500 text-sm mt-1">
						{t('simulator.thankYouPage.noScreenDescription')}
					</p>
				</div>
			</div>
		</div>
	)
}
